import { type ClientTarget } from '@features/clients';
import { Card, EmptyState, ErrorState, LoadingRows } from '@shared/ui';
import { useMemo } from 'react';

import { useFinalReport } from '../hooks/use-final-report';
import { buildFinalReport } from '../model/final-report';
import { isActive } from '../model/job-progress';

import { ReportDocument } from './ReportDocument';

import type { BulkModule } from '../schemas/bulk-migration';

/**
 * A launch's final report: the consolidated audit of a bulk migration, built
 * in the browser from the analysis, the launch and the newest finished
 * refresh. This part only loads; `ReportDocument` lays it out.
 *
 * Opened by `?view=final` on the launch, so the link is shareable.
 */
export function FinalReportView({
    target,
    module,
    environment,
    launchReportId,
}: {
    target: ClientTarget;
    module: BulkModule;
    environment: string;
    launchReportId: string;
}) {
    const sources = useFinalReport(target, module.moduleId, launchReportId);
    const launch = sources.launch.data;
    const data = sources.rows.data;

    const report = useMemo(
        () =>
            launch === undefined || data === undefined
                ? undefined
                : buildFinalReport({
                      module,
                      environment,
                      dryRun: sources.dryRun,
                      dryRunRows: data.dryRunRows,
                      launch,
                      launchRows: data.launchRows,
                      refresh: sources.refresh,
                      refreshRows: data.refreshRows,
                      refreshLookupLimited: sources.refreshLookupLimited,
                  }),
        [
            module,
            environment,
            launch,
            data,
            sources.dryRun,
            sources.refresh,
            sources.refreshLookupLimited,
        ],
    );

    if (sources.launch.isPending) return <LoadingRows label="Loading the migration…" />;
    if (sources.launch.isError) {
        return (
            <Card>
                <ErrorState
                    error={sources.launch.error}
                    onRetry={() => void sources.launch.refetch()}
                />
            </Card>
        );
    }
    if (launch !== undefined && isActive(launch.state)) {
        return (
            <Card>
                <EmptyState
                    title="Still launching"
                    description="The final report is available once every client has been started."
                />
            </Card>
        );
    }
    // The refresh is found in the job history: without it the report would
    // call every run unread.
    if (sources.jobs.isError) {
        return (
            <Card>
                <ErrorState
                    error={sources.jobs.error}
                    onRetry={() => void sources.jobs.refetch()}
                />
            </Card>
        );
    }
    if (sources.rows.isError) {
        return (
            <Card>
                <ErrorState
                    error={sources.rows.error}
                    onRetry={() => void sources.rows.refetch()}
                />
            </Card>
        );
    }
    if (report === undefined) return <LoadingRows label="Reading every client…" />;

    return (
        <ReportDocument
            target={target}
            module={module}
            report={report}
            jobs={sources.jobs.data ?? []}
            launchReportId={launchReportId}
        />
    );
}
